import React from 'react';
import { MapPin, FileText, Search, Compass, Map, CheckSquare, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Reveal, RevealGroup, RevealItem } from '../components/motion/Reveal';
import Seo from '../components/Seo';

const Services = () => {
  const services = [
    {
      icon: MapPin,
      title: "Cadastral & Topographic Surveying",
      description: "Comprehensive land surveying using modern equipment and techniques for precise property boundaries and topographic details.",
      features: [
        "Property boundary surveys",
        "Topographic mapping",
        "GPS & total station measurements",
        "Accurate coordinate determination"
      ]
    },
    {
      icon: FileText,
      title: "Survey Plan Preparation",
      description: "Professional preparation of survey plans that meet all regulatory requirements with government authentication.",
      features: [
        "Survey plan drafting",
        "Government authentication",
        "Surveyor General processing",
        "Compliance with regulations"
      ]
    },
    {
      icon: Search,
      title: "Survey Search & Verification",
      description: "Thorough investigation and verification of land status, title documents, and survey history.",
      features: [
        "Document verification",
        "Land title search",
        "Government acquisition checks",
        "Encumbrance searches"
      ]
    },
    {
      icon: Compass,
      title: "Boundary Identification",
      description: "Expert identification and physical marking of property boundaries with dispute resolution support.",
      features: [
        "Physical boundary demarcation",
        "Dispute resolution support",
        "Lost beacon re-establishment",
        "Perimeter measurements"
      ]
    },
    {
      icon: Map,
      title: "Property Documentation",
      description: "Comprehensive assistance with all property documentation requirements and registration processes.",
      features: [
        "C of O processing support",
        "Survey plan renewal",
        "Land registry assistance",
        "Title perfection guidance"
      ]
    },
    {
      icon: CheckSquare,
      title: "Land Status Checks",
      description: "Thorough verification of land status ensuring property is free from government acquisition.",
      features: [
        "Government gazette searches",
        "Acquisition status verification",
        "Right of occupancy validation",
        "Legal status reports"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Surveying Services in Lagos | Versatile Geopick NIG Ltd"
        description="Cadastral and topographic surveys, survey plan preparation, survey search and verification, boundary identification and property documentation in Lagos."
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-stone-100 via-amber-50/30 to-stone-50 pt-24">
        <Reveal className="text-center px-6" duration={0.9}>
          <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3">What We Do</p>
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 tracking-tight">
            Our <span className="font-semibold text-emerald-700">Services</span>
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mt-6">
            Comprehensive surveying solutions for all your land and property needs
          </p>
        </Reveal>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <RevealItem key={index}>
                <Card 
                  className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group"
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-colors duration-300">
                      <Icon className="h-7 w-7 text-emerald-700" />
                    </div>
                    <h3 className="text-2xl font-medium text-stone-800 mb-4">{service.title}</h3>
                    <p className="text-stone-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-stone-600">
                          <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full flex-shrink-0 mt-2"></div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gradient-to-br from-stone-50 to-amber-50/20">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-20">
            <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3">How We Work</p>
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 tracking-tight">
              Simple Process
            </h2>
          </Reveal>

          <RevealGroup className="grid md:grid-cols-4 gap-8" stagger={0.15}>
            {[
              { step: "01", title: "Consultation", desc: "Initial discussion of your needs" },
              { step: "02", title: "Site Visit", desc: "Professional field assessment" },
              { step: "03", title: "Documentation", desc: "Plan preparation & processing" },
              { step: "04", title: "Delivery", desc: "Authenticated documents delivered" }
            ].map((item, index) => (
              <RevealItem key={index} direction="scale" className="text-center">
                <div className="w-16 h-16 bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-light text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-medium text-stone-800 mb-3">{item.title}</h3>
                <p className="text-stone-600">{item.desc}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-emerald-700">
        <Reveal className="max-w-4xl mx-auto px-6 text-center" direction="scale">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-8 leading-tight">
            Ready to get started?
          </h2>
          <p className="text-xl text-emerald-100 mb-10">
            Contact us today for a professional consultation
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              className="bg-white text-emerald-700 hover:bg-stone-50 px-10 py-6 text-lg font-normal shadow-xl hover:shadow-2xl transition-all duration-300"
              asChild
            >
              <Link to="/contact">
                Get In Touch <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-emerald-700 px-10 py-6 text-lg font-normal transition-all duration-300"
              asChild
            >
              <a href="https://wa.me/2348034494507" target="_blank" rel="noopener noreferrer">
                WhatsApp Us
              </a>
            </Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Services;
